import { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Calendar, AlertTriangle } from "lucide-react";
import { PROVINCE_ITALIANE } from "@/constants/province";
import { PAESI } from "@/constants/paesi";

const GROUP_STYLE = {
  anagrafici: "border-[#1A2B4A]/15 bg-[#f9f8f5]",
  documenti: "border-[#C9A84C]/35 bg-[#fbf9f3]",
  altri: "border-[#1A2B4A]/10 bg-white",
};

// Dati minimi per una scheda cliente
export const CLIENTE_REQUIRED = ["nome", "cognome", "luogo_nascita", "data_nascita", "nazionalita", "residenza_tipo"];
const RESIDENZA_ITALIA = ["indirizzo", "residenza_citta", "residenza_provincia", "residenza_cap"];
const RESIDENZA_ESTERO = ["residenza_stato"];
const SOLO_ITALIA = ["residenza_provincia", "residenza_cap"];
const SOLO_ESTERO = ["residenza_stato"];
const FULL_WIDTH = ["residenza_tipo"];

// Chiavi obbligatorie in base alla residenza scelta
export const requisitiCliente = (val) => {
  const tipo = (val("residenza_tipo") || "").toLowerCase();
  return [...CLIENTE_REQUIRED, ...(tipo === "italia" ? RESIDENZA_ITALIA : tipo === "estero" ? RESIDENZA_ESTERO : [])];
};

// Campi che non hanno senso con la residenza scelta: non vanno mai richiesti
export const campiResidenzaNonPertinenti = (residenzaTipo) => {
  const t = String(residenzaTipo || "").toLowerCase();
  if (t === "italia") return SOLO_ESTERO;
  if (t === "estero") return SOLO_ITALIA;
  return [];
};

const DATE_KEYS = new Set([
  "data_nascita", "data_rilascio", "data_scadenza",
  "scadenza_documento", "rilascio_documento", "scadenza_permesso", "data_emissione_permesso",
]);

// gg/mm/aaaa <-> aaaa-mm-gg
const isoToIt = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const itToIso = (it) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(it || "");
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};
// Inserisce le "/" automaticamente mentre si digita (gg/mm/aaaa)
const maskDate = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length > 2) out += "/" + d.slice(2, 4);
  if (d.length > 4) out += "/" + d.slice(4, 8);
  return out;
};

// Città italiana con suggerimenti: compila da sola provincia e CAP
export function ComuneField({ value, onChange, onPick, testid, danger = false }) {
  const [q, setQ] = useState("");
  const [lista, setLista] = useState([]);
  const [aperto, setAperto] = useState(false);
  useEffect(() => {
    if (!aperto || q.trim().length < 2) { setLista([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/comuni?q=${encodeURIComponent(q.trim())}`);
        setLista(data.comuni || []);
      } catch { setLista([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, aperto]);
  return (
    <div className="relative">
      <input data-testid={testid} value={value || ""} autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setQ(e.target.value); setAperto(true); }}
        onFocus={() => setAperto(true)}
        onBlur={() => setTimeout(() => setAperto(false), 150)}
        className={`w-full mt-1 px-2.5 py-1.5 text-sm bg-white border rounded-sm focus:outline-none ${danger ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`} />
      {aperto && lista.length > 0 && (
        <ul data-testid={`${testid}-suggestions`}
          className="absolute z-30 left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white border border-[#1A2B4A]/15 rounded-sm shadow-md">
          {lista.map((c) => (
            <li key={`${c.nome}-${c.provincia}`}>
              <button type="button" data-testid={`${testid}-opt-${c.nome}`}
                onMouseDown={(e) => { e.preventDefault(); onPick(c); setAperto(false); setLista([]); }}
                className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-[#f9f8f5] flex items-center justify-between gap-2">
                <span>{c.nome}</span>
                <span className="text-[11px] text-[#8a93a6]">{c.provincia}{c.cap.length === 1 ? ` · ${c.cap[0]}` : ` · ${c.cap.length} CAP`}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const INPUT_CLS = "w-full mt-1 px-2.5 py-1.5 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]";

// Campo data: digitazione manuale con "/" automatica + icona calendario per il picker nativo
export function DateField({ value, onChange, testid, required = false }) {
  const dateRef = useRef(null);
  const openPicker = () => {
    const el = dateRef.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); }
  };
  const complete = /^\d{2}\/\d{2}\/\d{4}$/.test(value || "");
  const invalid = complete && (() => {
    const [d, m, y] = (value || "").split("/").map(Number);
    if (m < 1 || m > 12 || d < 1 || y < 1900 || y > 2100) return true;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d;
  })();
  const danger = invalid || (required && !String(value || "").trim());
  return (
    <div className="relative mt-1">
      <div className="relative">
        <input data-testid={testid} value={value || ""} inputMode="numeric" placeholder="gg/mm/aaaa" maxLength={10}
          onChange={(e) => onChange(maskDate(e.target.value))}
          className={`w-full px-2.5 py-1.5 pr-9 text-sm bg-white border rounded-sm focus:outline-none ${danger ? "border-[#9b2c2c] focus:border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`} />
        <button type="button" tabIndex={-1} data-testid={`${testid}-calendar`} onClick={openPicker} title="Apri calendario"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#4a5568] hover:text-[#C9A84C]">
          <Calendar className="w-4 h-4" />
        </button>
        <input ref={dateRef} type="date" tabIndex={-1} aria-hidden value={itToIso(value)}
          onChange={(e) => onChange(isoToIt(e.target.value))}
          className="absolute right-0 top-0 h-full w-9 opacity-0" />
      </div>
      {invalid && <p data-testid={`${testid}-error`} className="text-[11px] text-[#9b2c2c] mt-0.5">Data non valida (usa gg/mm/aaaa)</p>}
    </div>
  );
}

// getValue(field) -> string ; setValue(field, value)
// I gruppi "contatti" (Nome, Cognome, Email, Telefono) e "azienda" (PEC, Rappresentante
// legale) sono esclusi: hanno già un blocco dedicato nel form anagrafica.
// tipiPratica: array di id tipo pratica di cui mostrare i campi personalizzati
//   - undefined  -> tutti i campi (comportamento storico)
//   - []         -> solo predefiniti + personalizzati validi per tutti i tipi
// clienteId: ricava i tipi dalle pratiche del cliente (usato nell'anagrafica cliente)
export default function AnagraficaFieldsGroups({ getValue, setValue, requiredKeys = [], tipiPratica, clienteId, onlyCustom = false, onlyBuiltin = false, baseRequired = false }) {
  const qs = tipiPratica !== undefined
    ? `?tipi=${(tipiPratica || []).join(",")}`
    : (clienteId ? `?cliente_id=${clienteId}` : "");
  const { data } = useQuery({
    queryKey: ["anagrafica-fields", qs],
    queryFn: async () => (await api.get(`/anagrafica-fields${qs}`)).data,
  });
  const all = data?.fields || [];
  const fields = onlyCustom ? all.filter((f) => !f.builtin) : (onlyBuiltin ? all.filter((f) => f.builtin) : all);
  const byKeyField = {};
  all.forEach((f) => { byKeyField[f.key] = f; });
  const valOf = (key) => (byKeyField[key] ? getValue(byKeyField[key]) : "") || "";
  const residenza = String(valOf("residenza_tipo") || "").toLowerCase();
  const [capDisponibili, setCapDisponibili] = useState([]);
  const setByKey = (key, v) => { if (byKeyField[key]) setValue(byKeyField[key], v); };
  const scegliComune = (c) => {
    setByKey("residenza_citta", c.nome);
    setByKey("residenza_provincia", c.provincia);
    setCapDisponibili(c.cap || []);
    if ((c.cap || []).length === 1) setByKey("residenza_cap", c.cap[0]);
    else setByKey("residenza_cap", "");
  };

  const req = new Set([...requiredKeys, ...(baseRequired ? requisitiCliente(valOf) : [])]);
  const nascosto = (key) => (residenza === "italia" && SOLO_ESTERO.includes(key))
    || (residenza === "estero" && SOLO_ITALIA.includes(key))
    || (!residenza && [...SOLO_ITALIA, ...SOLO_ESTERO, "indirizzo", "residenza_citta"].includes(key));

  const groups = (data?.groups || []).filter((g) => g.key !== "azienda");
  const byGroup = {};
  fields.filter((f) => !nascosto(f.key)).forEach((f) => {
    (byGroup[f.group || "altri"] = byGroup[f.group || "altri"] || []).push(f);
  });
  const orderedGroups = groups.filter((g) => (byGroup[g.key] || []).length > 0);

  // Alert sui dati facoltativi non compilati (documenti e altri dati)
  const facoltativiMancanti = fields.filter((f) => !req.has(f.key) && !nascosto(f.key)
    && (f.group === "documenti" || f.group === "altri") && !String(getValue(f) || "").trim());

  return (
    <div className="space-y-4">
      {orderedGroups.map((g) => (
        <div key={g.key} data-testid={`ana-group-${g.key}`} className={`rounded-lg border p-4 ${GROUP_STYLE[g.key] || GROUP_STYLE.altri}`}>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#1A2B4A] mb-3">{g.label}</h4>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">
            {byGroup[g.key].map((f) => {
              const val = getValue(f) || "";
              const isReq = req.has(f.key);
              const missing = isReq && !String(val).trim();
              if (f.key === "residenza_tipo") {
                return (
                  <div key={f.key} className="sm:col-span-2">
                    <label className="text-xs text-[#4a5568]">Residenza<span className="text-[#9b2c2c]"> *</span></label>
                    <div className="flex gap-2 mt-1">
                      {[["italia", "Italia"], ["estero", "Estero"]].map(([v, label]) => (
                        <button key={v} type="button" data-testid={`ana-residenza-${v}`} onClick={() => setValue(f, v)}
                          className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${
                            residenza === v ? "bg-[#1A2B4A] text-white border-[#1A2B4A]"
                              : `bg-white text-[#1A2B4A] hover:border-[#C9A84C] ${missing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20"}`}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    {missing && <p className="text-[11px] text-[#9b2c2c] mt-1">Indica se il cliente risiede in Italia o all'estero</p>}
                  </div>
                );
              }
              return (
                <div key={f.key} className={FULL_WIDTH.includes(f.key) ? "sm:col-span-2" : ""}>
                  <label className="text-xs text-[#4a5568]">{f.label}
                    {isReq && <span className="text-[#9b2c2c]" title="Obbligatorio"> *</span>}
                    {!f.builtin && <span className="text-[#C9A84C]"> ·</span>}
                  </label>
                  {f.key === "residenza_citta" && residenza === "italia" ? (
                    <>
                      <ComuneField testid={`ana-${f.key}`} value={val} danger={missing}
                        onChange={(v) => setValue(f, v)} onPick={scegliComune} />
                      <p className="text-[10.5px] text-[#8a93a6] mt-0.5">Scegli la città dai suggerimenti: provincia e CAP si compilano da soli</p>
                    </>
                  ) : f.key === "nazionalita" ? (
                    <select data-testid={`ana-${f.key}`} value={val} onChange={(e) => setValue(f, e.target.value)}
                      className={`w-full mt-1 px-2.5 py-1.5 text-sm bg-white border rounded-sm focus:outline-none ${missing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`}>
                      <option value="">— seleziona il paese —</option>
                      {val && !PAESI.includes(val) && <option value={val}>{val}</option>}
                      {PAESI.map((pa) => <option key={pa} value={pa}>{pa}</option>)}
                    </select>
                  ) : f.key === "residenza_cap" && capDisponibili.length > 1 ? (
                    <>
                      <input data-testid={`ana-${f.key}`} list="cap-disponibili" value={val} inputMode="numeric" maxLength={5}
                        onChange={(e) => setValue(f, e.target.value)}
                        className={`w-full mt-1 px-2.5 py-1.5 text-sm bg-white border rounded-sm focus:outline-none ${missing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`} />
                      <datalist id="cap-disponibili">
                        {capDisponibili.map((cp) => <option key={cp} value={cp} />)}
                      </datalist>
                      <p className="text-[10.5px] text-[#8a93a6] mt-0.5">{capDisponibili.length} CAP disponibili per questa città: scegli il tuo</p>
                    </>
                  ) : f.key === "residenza_provincia" ? (
                    <select data-testid={`ana-${f.key}`} value={val} onChange={(e) => setValue(f, e.target.value)}
                      className={`w-full mt-1 px-2.5 py-1.5 text-sm bg-white border rounded-sm focus:outline-none ${missing ? "border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`}>
                      <option value="">— seleziona —</option>
                      {PROVINCE_ITALIANE.map((pv) => <option key={pv} value={pv}>{pv}</option>)}
                    </select>
                  ) : DATE_KEYS.has(f.key) ? (
                    <DateField testid={`ana-${f.key}`} value={val} onChange={(v) => setValue(f, v)} required={missing} />
                  ) : (
                    <input data-testid={`ana-${f.key}`} value={val} onChange={(e) => setValue(f, e.target.value)}
                      inputMode={f.key === "residenza_cap" ? "numeric" : undefined}
                      maxLength={f.key === "residenza_cap" ? 5 : undefined}
                      className={`w-full mt-1 px-2.5 py-1.5 text-sm bg-white border rounded-sm focus:outline-none ${missing ? "border-[#9b2c2c] focus:border-[#9b2c2c]" : "border-[#1A2B4A]/20 focus:border-[#C9A84C]"}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {baseRequired && facoltativiMancanti.length > 0 && (
        <div data-testid="ana-alert-facoltativi"
          className="flex items-start gap-2 rounded-md border border-[#C9A84C]/50 bg-[#fbf7ec] px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-[#8a6d1f] shrink-0 mt-0.5" />
          <p className="text-[11px] text-[#5c4a12] leading-relaxed">
            <b>{facoltativiMancanti.length} dati facoltativi non compilati</b> (puoi salvare comunque):{" "}
            {facoltativiMancanti.map((f) => f.label).join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}
